import {useAgentAuthorizationGuard} from '@github-ui/agent-authorization-dialog/guard'
import {useAgentAuthorizationStatuses} from '@github-ui/agent-authorization-dialog/statuses'
import type {FormCustomizations} from '@github-ui/agent-sessions/components/copilot-assignment/copilot-assignment-types'
import {CopilotAssignmentButton} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentButton'
import {CopilotAssignmentModal} from '@github-ui/agent-sessions/components/copilot-assignment/CopilotAssignmentModal'
import {getSessionStatusIcon} from '@github-ui/agent-sessions/components/StatusIcon'
import {isSessionState, type SelectedAgent, SessionState} from '@github-ui/agent-sessions/types/session'
import {detectNewlyAddedAgents} from '@github-ui/agent-sessions/utils/agent-helpers'
import type {AgentTaskInfo} from '@github-ui/assignees'
import {Assignees} from '@github-ui/assignees'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  type IssueAgentTask,
  type IssueAssignedAgentInfo,
  useIssueAgentTasks,
} from '@github-ui/issue-agent-tasks/contexts/IssueAgentTasksContext'
import {useIsVisualizationEnabled} from '@github-ui/issue-agent-tasks/utils/is-visualization-enabled'
import {commitUpdateIssueAssigneesMutation} from '@github-ui/item-picker/commitUpdateIssueAssigneesMutation'
import {DEFAULT_MAXIMUM_ASSIGNEES} from '@github-ui/item-picker-assignee'
import type {AssigneeIssuePickerProps} from '@github-ui/item-picker-assignee-relay'
import {AssigneePicker} from '@github-ui/item-picker-assignee-relay'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {noop} from '@github-ui/noop'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import {Button} from '@primer/react'
import {type RefObject, useCallback, useMemo, useReducer, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-view-query-types'
import type {IssueNode, IssueNodeRepository} from '../issue-viewer-view-query-types'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import styles from './AssigneesSection.module.css'
import {useAgentAssignmentComplete} from './use-agent-assignment-complete'

export const ReadonlyAssigneesSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.assignees} />

// Default task info shown when an agent has been assigned but no task exists yet
const defaultQueuedTaskInfo: AgentTaskInfo = {
  statusIcon: getSessionStatusIcon(SessionState.Queued),
  count: 0,
}

// Helper function to compare two arrays of assignees by their IDs
const assigneesEqual = (a: AssigneePickerAssignee[], b: AssigneePickerAssignee[]): boolean => {
  if (a.length !== b.length) return false
  const aIds = new Set(a.map(assignee => assignee.id))
  return b.every(assignee => aIds.has(assignee.id))
}

// RELAY-CLEANUP(IssueViewerSecondaryViewQuery): widens nullable fields so the still-Relay parent
// data is assignable. Remove when IssueViewerSecondaryViewQuery leaves Relay.
export type AssigneesSectionLazyFragment = RelayCompat<Pick<IssueSecondaryNode, 'suggestedActors'>>

// Relay-compatible shape for the `assigner` field: IssueNode uses a discriminated union
// ({__typename:'Bot'|'User'|...}), but the still-Relay parent normalizes it to this flat form.
type AgentAssignmentAssigner = {
  readonly __typename: string
  readonly id?: string
  readonly login: string
}
// Omit `__typename` (Relay doesn't add it for concrete types unless selected) and override
// `assigner` with the Relay-compatible flat shape defined above.
type AgentAssignmentItem = Omit<NonNullable<IssueNode['agentAssignments'][number]>, '__typename' | 'assigner'> & {
  readonly assigner: AgentAssignmentAssigner | null
}

// RELAY-CLEANUP(AssigneesSectionFragment): RelayCompat widens nullable scalar fields from
// `assignedActors` (e.g. `| null` → `| null | undefined`) so the still-Relay parent data is
// assignable. Remove when AssigneesSectionFragment leaves Relay.
export type AssigneesSectionAssigneesFragment = RelayCompat<{
  readonly assignedActors: IssueNode['assignedActors']
  readonly agentAssignments: readonly AgentAssignmentItem[]
}>

// RELAY-CLEANUP(AssigneesSectionFragment): widens nullable fields so the still-Relay parent
// data is assignable. Remove when AssigneesSectionFragment leaves Relay.
export type AssigneesSectionCopilotRepositoryFragment = RelayCompat<
  Pick<IssueNodeRepository, 'isArchived' | 'isCopilotAgentEnabled'>
>

type AssigneesSectionIssueRepository = Pick<IssueNodeRepository, 'name' | 'isArchived' | 'isCopilotAgentEnabled'> & {
  readonly owner: Pick<NonNullable<IssueNodeRepository['owner']>, 'login'>
}

export type AssigneesSectionFragment = Pick<IssueNode, 'id' | 'number' | 'assignedActors' | 'viewerCanAssign'> & {
  readonly agentAssignments: readonly AgentAssignmentItem[]
  readonly repository: AssigneesSectionIssueRepository
}

type AssigneesSectionProps = {
  sectionHeader: React.ReactElement
  onSelfAssignClick: () => void
  assignees: AssigneePickerAssignee[]
  agentAssignments?: AssigneesSectionAssigneesFragment['agentAssignments']
  issueAgentTasks?: IssueAgentTask[]
  agents?: IssueAssignedAgentInfo[]
  isAgentTasksLoading?: boolean
  readonly: boolean
  className?: string
  copilotAssignmentButton?: React.ReactElement
}
export const AssigneesSection = ({
  sectionHeader,
  onSelfAssignClick,
  assignees,
  agentAssignments,
  issueAgentTasks,
  agents,
  isAgentTasksLoading,
  readonly,
  copilotAssignmentButton,
}: AssigneesSectionProps) => {
  const visualizationEnabled = useIsVisualizationEnabled()
  const emptyHeader = !readonly ? (
    <>
      {LABELS.emptySections.assignees(true)}
      <Button variant="link" onClick={onSelfAssignClick} className={styles.selfAssignButton}>
        {LABELS.emptySections.selfAssign}
      </Button>
    </>
  ) : (
    LABELS.emptySections.assignees(false)
  )

  // Wrap the copilot assignment button to match the styling of other section buttons like Unsubscribe
  const sectionFooter = copilotAssignmentButton ? (
    <div className="width-full d-flex flex-column flex-items-stretch pt-2 px-2">
      <div>{copilotAssignmentButton}</div>
    </div>
  ) : undefined

  // Map agent task that we have loaded to the agents assigned to the issue so that we can show task status in the UI.
  // We only show the most recent task for each agent.
  const agentTaskMap = useMemo(() => {
    const map = new Map<string, AgentTaskInfo>()
    if (!issueAgentTasks?.length || !agents?.length) return map

    for (const agent of agents) {
      const agentTasks = issueAgentTasks.filter(task => task.agent_collaborators?.some(c => c.agent_id === agent.id))
      if (!agentTasks.length) continue

      // only use the most recent status as the "summary" in the view
      const mostRecent = agentTasks.reduce((latest, task) => {
        const taskTime = new Date(task.updated_at ?? task.last_updated_at ?? '').getTime()
        const latestTime = new Date(latest.updated_at ?? latest.last_updated_at ?? '').getTime()
        return taskTime > latestTime ? task : latest
      })

      const mostRecentState = mostRecent.state ?? mostRecent.status
      if (agent.bot_global_relay_id && isSessionState(mostRecentState)) {
        map.set(agent.bot_global_relay_id, {
          statusIcon: getSessionStatusIcon(mostRecentState),
          count: agentTasks.length,
          taskUrl: mostRecent.url,
          taskId: mostRecent.id,
          repoId: mostRecent.repository.id,
        })
      }
    }

    return map
  }, [issueAgentTasks, agents])

  const assigneeList = useMemo(() => {
    const baseAssignees = assignees.map(assignee => ({
      ...assignee,
      displayName: assignee.displayName || assignee.login,
      isCopilot: assignee.isCopilot ?? false,
      isAgent: assignee.isAgent ?? false,
    }))

    if (!visualizationEnabled || !agentAssignments?.length) {
      return baseAssignees
    }

    // Filter to agent assignments where both agent and assigner are still in assignedActors
    const assignedLogins = new Set(assignees.map(a => a.login))
    const validAssignments = agentAssignments.filter(
      a =>
        a?.assignee?.login &&
        a?.assigner?.login &&
        assignedLogins.has(a.assignee.login) &&
        assignedLogins.has(a.assigner.login),
    )

    if (!validAssignments.length) return baseAssignees

    const agentIds = new Set(validAssignments.map(a => a?.assignee?.id))

    return baseAssignees
      .filter(a => !agentIds.has(a.id))
      .map(assignee => ({
        ...assignee,
        assignedAgents: validAssignments
          .filter(a => a?.assigner?.login === assignee.login)
          .map(a => ({
            id: a?.assignee?.id ?? '',
            login: a?.assignee?.login ?? '',
            avatarUrl: a?.assignee?.avatarUrl ?? '',
            name: a?.assignee?.displayName ?? a?.assignee?.login ?? null,
            displayName: a?.assignee?.displayName ?? a?.assignee?.login ?? '',
            profileResourcePath: `/apps/${a?.assignee?.login}`,
            isCopilot: a?.assignee?.isCopilot ?? false,
            isAgent: a?.assignee?.isAgent ?? true,
            // Show queued clock icon by default when agent has an assignment but no task yet.
            // Do not show during loading — we don't know yet if a task exists.
            agentTaskInfo:
              agentTaskMap.get(a?.assignee?.id ?? '') ?? (isAgentTasksLoading ? undefined : defaultQueuedTaskInfo),
          })),
      }))
  }, [assignees, agentAssignments, agentTaskMap, visualizationEnabled, isAgentTasksLoading])

  return (
    <Section
      id={`sidebar-assignees-section`}
      sectionHeader={sectionHeader}
      sectionFooter={sectionFooter}
      emptyText={assignees.length > 0 ? undefined : emptyHeader}
    >
      <Assignees assignees={assigneeList} testId={TEST_IDS.assignees} />
    </Section>
  )
}

export type EditIssueAssigneesSectionProps = {
  issue: AssigneesSectionFragment
  // The viewer is read as plain props (formerly the AssigneesSectionViewer Relay fragment). It is
  // consumed as a potential self-assignee, so it reuses the AssigneePickerAssignee shape.
  // null is for when the user is signed out
  viewer: AssigneePickerAssignee | null
  lazyKey?: AssigneesSectionLazyFragment
  onIssueUpdate?: () => void
  insideSidePanel?: boolean
  readonly: boolean
}

/**
 * Manages the sequential agent assignment queue.
 * - 'start': initializes the queue when agents are selected from the picker
 * - 'advance': moves to the next agent on modal close, carrying form customizations forward
 */
type AgentQueueState = {
  currentAgent?: SelectedAgent
  remaining: SelectedAgent[]
  total: number
  lastFormCustomizations?: FormCustomizations
}

type AgentQueueAction =
  | {type: 'start'; agents: SelectedAgent[]}
  | {type: 'advance'; formCustomizations?: FormCustomizations}

const EMPTY_QUEUE: AgentQueueState = {currentAgent: undefined, remaining: [], total: 0}

function agentQueueReducer(state: AgentQueueState, action: AgentQueueAction): AgentQueueState {
  switch (action.type) {
    case 'start': {
      const [first, ...rest] = action.agents
      return first ? {currentAgent: first, remaining: rest, total: action.agents.length} : EMPTY_QUEUE
    }
    case 'advance': {
      const [next, ...rest] = state.remaining
      // Prefer freshly-passed customizations; fall back to previously saved ones
      const customizations = action.formCustomizations ?? state.lastFormCustomizations
      return next
        ? {...state, currentAgent: next, remaining: rest, lastFormCustomizations: customizations}
        : EMPTY_QUEUE
    }
  }
}

export function EditIssueAssigneesSection({
  issue: data,
  viewer: viewerData,
  lazyKey,
  onIssueUpdate,
  insideSidePanel,
  readonly,
}: EditIssueAssigneesSectionProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const {
    repository,
    repository: {
      owner: {login: owner},
      name: repo,
      isArchived: isRepositoryArchived,
    },
    number,
  } = data

  const {id: issueId, viewerCanAssign} = data

  const suggestions: AssigneePickerAssignee[] = useMemo(() => {
    return (lazyKey?.suggestedActors?.nodes || []).flatMap(actor => (actor ? [actor] : []))
  }, [lazyKey?.suggestedActors?.nodes])

  const assignees: AssigneePickerAssignee[] = useMemo(() => {
    return (data.assignedActors?.nodes || []).flatMap(actor => (actor ? [actor] : []))
  }, [data.assignedActors?.nodes])

  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()
  // The Assignees section header edit button (the picker anchor). Used as the
  // return-focus target for the Copilot assignment modal so focus comes back to
  // the persistent Assignees control regardless of how the modal was opened.
  // See https://github.com/github/accessibility-audits/issues/14388
  const assigneePickerAnchorRef = useRef<HTMLButtonElement | null>(null)
  const nwo = `${owner}/${repo}`
  const authorizationGuard = useAgentAuthorizationGuard({nwo})
  // Real per-agent status from the REST agents endpoint when the flag is on; empty map otherwise.
  const {data: agentAuthStatuses} = useAgentAuthorizationStatuses(nwo)

  // Enrich agent rows with `requiresAuthentication` for the picker warning icon.
  // Status precedence: API `needs_authorization` > mock guard.
  const enrichWithAuthState = useCallback(
    (list: AssigneePickerAssignee[]): AssigneePickerAssignee[] =>
      list.map(item => {
        if (!item.isAgent && !item.isCopilot) return item
        const displayName = item.name ?? item.displayName ?? item.login
        const apiStatus = agentAuthStatuses.get(item.login)
        const requiresAuthentication =
          apiStatus?.needsAuthorization ?? authorizationGuard.requiresAuth(item.login, displayName, item.avatarUrl)
        return requiresAuthentication ? {...item, requiresAuthentication: true} : item
      }),
    [agentAuthStatuses, authorizationGuard],
  )
  const enrichedSuggestions = useMemo(() => enrichWithAuthState(suggestions), [enrichWithAuthState, suggestions])
  const enrichedAssignees = useMemo(() => enrichWithAuthState(assignees), [enrichWithAuthState, assignees])

  const {data: issueAgentTasksData, isPending: isAgentTasksPending} = useIssueAgentTasks()

  const visualizationEnabled = useIsVisualizationEnabled()
  const readonlyAssignee = readonly || !viewerCanAssign || isRepositoryArchived

  const canUseCopilotAssignment = useMemo(() => {
    const isCopilotCurrentlyAssigned = assignees.some(assignee => isCopilot(assignee.login))
    return !readonlyAssignee && repository.isCopilotAgentEnabled && !isCopilotCurrentlyAssigned
  }, [assignees, readonlyAssignee, repository.isCopilotAgentEnabled])

  const canUseAgentAssignment = !readonlyAssignee && repository.isCopilotAgentEnabled

  // State for managing Copilot modal triggered from both button and assignee picker
  const [copilotModalOpen, setCopilotModalOpen] = useState(false)
  const [pickerInstanceKey, setPickerInstanceKey] = useState(0)
  const [agentQueue, dispatchQueue] = useReducer(agentQueueReducer, EMPTY_QUEUE)

  const handleOpenCopilotModal = useCallback(() => {
    setCopilotModalOpen(true)
    /* Force a remount of the picker so it resets to initialSelectedItems (equivalent to Cancel)
      This is needed because when we do nothing with Copilot, it remains selected in the picker
        and the modal would reopen immediately when the picker opens */
    setPickerInstanceKey(prev => prev + 1)
  }, [setCopilotModalOpen])

  // Close modal if queue is exhausted, then advance to next agent (or clear queue)
  const handleCloseCopilotModal = useCallback(
    (formCustomizations?: FormCustomizations) => {
      if (agentQueue.remaining.length === 0) {
        setCopilotModalOpen(false)
      }
      dispatchQueue({type: 'advance', formCustomizations})
    },
    [agentQueue.remaining.length],
  )

  const updateAssignees = useCallback(
    (
      selectedAssignees: AssigneePickerAssignee[],
      newAgentAssignments?: AssigneesSectionAssigneesFragment['agentAssignments'],
    ) => {
      const existingAgentAssignments = visualizationEnabled ? data.agentAssignments : undefined
      commitUpdateIssueAssigneesMutation({
        environment,
        input: {issueId, assignedActors: selectedAssignees, participants: suggestions},
        existingAgentAssignments: newAgentAssignments?.length
          ? [...(existingAgentAssignments ?? []), ...newAgentAssignments]
          : existingAgentAssignments,
        onError: () =>
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateAssignees,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [addToast, environment, issueId, onIssueUpdate, suggestions, data.agentAssignments, visualizationEnabled],
  )

  const updateAssigneesWithCopilotDetection = useCallback(
    (selectedAssignees: AssigneePickerAssignee[]) => {
      // Calculate if Copilot was previously assigned
      const wasCopilotPreviouslyAssigned = assignees.some(assignee => isCopilot(assignee.login))

      // Check if Copilot is in the selection and feature is enabled
      const hasCopilotAssignee = selectedAssignees.some(assignee => assignee.isCopilot)
      const nonCopilotAssignees = selectedAssignees.filter(assignee => !assignee.isCopilot)

      let assigneesToSave = canUseCopilotAssignment ? nonCopilotAssignees : selectedAssignees
      let newAgentAssignments: AssigneesSectionAssigneesFragment['agentAssignments'] | undefined

      // Add viewer to assignees when a 3rd party agent is being added
      if (viewerData) {
        // Check if a 3rd party agent (non-Copilot) is being added
        const previousAgentLogins = new Set(assignees.filter(a => a.isAgent && !isCopilot(a.login)).map(a => a.login))
        const thirdPartyAgentsAdded = selectedAssignees.filter(
          assignee => assignee.isAgent && !isCopilot(assignee.login) && !previousAgentLogins.has(assignee.login),
        )

        if (thirdPartyAgentsAdded.length > 0) {
          const isViewerAlreadyAssigned = assignees.some(assignee => assignee.login === viewerData.login)
          const isViewerInSelection = selectedAssignees.some(assignee => assignee.login === viewerData.login)

          const shouldAddViewer =
            !isViewerAlreadyAssigned && !isViewerInSelection && selectedAssignees.length < DEFAULT_MAXIMUM_ASSIGNEES

          if (shouldAddViewer) {
            assigneesToSave = [...assigneesToSave, viewerData]
          }

          newAgentAssignments = thirdPartyAgentsAdded.map(agent => ({
            assignee: {
              id: agent.id,
              login: agent.login,
              avatarUrl: agent.avatarUrl,
              isCopilot: agent.isCopilot ?? false,
              isAgent: agent.isAgent ?? true,
              displayName: agent.displayName ?? agent.login,
            },
            assigner: {
              __typename: viewerData.__typename,
              id: viewerData.id,
              login: viewerData.login,
            },
          }))
        }
      }

      // Only check for unnecessary mutations when Copilot assignment is enabled
      // When disabled, always save to maintain existing behavior
      if (canUseCopilotAssignment) {
        // Only save if the resulting assignees list is different from current assignees
        // This prevents unnecessary mutations when only Copilot is removed/added
        const currentNonCopilotAssignees = assignees.filter(assignee => !isCopilot(assignee.login))

        if (!assigneesEqual(assigneesToSave, currentNonCopilotAssignees)) {
          updateAssignees(assigneesToSave, newAgentAssignments)
        }
      } else {
        updateAssignees(assigneesToSave, newAgentAssignments)
      }

      // Only open modal if Copilot is being ADDED (not removed)
      // This checks: Copilot is in new selection AND was not previously assigned
      if (hasCopilotAssignee && canUseCopilotAssignment && !wasCopilotPreviouslyAssigned) {
        handleOpenCopilotModal()
      }
    },
    [assignees, canUseCopilotAssignment, updateAssignees, handleOpenCopilotModal, viewerData],
  )

  const updateAssigneesWithAgentDetection = useCallback(
    (selectedAssignees: AssigneePickerAssignee[]) => {
      if (!canUseAgentAssignment) {
        updateAssignees(selectedAssignees)
        return
      }

      const {newlyAddedAgents, nonAgentAssignees} = detectNewlyAddedAgents(
        assignees,
        selectedAssignees,
        a => !!a.isAgent || !!a.isCopilot,
        a => a.login,
      )

      if (newlyAddedAgents.length > 0) {
        if (!assigneesEqual(nonAgentAssignees, assignees)) {
          updateAssignees(nonAgentAssignees)
        }

        const agentsToProcess = isFeatureEnabled('issue_cca_multi_assign_modal')
          ? newlyAddedAgents
          : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            [newlyAddedAgents[0]!]
        dispatchQueue({
          type: 'start',
          agents: agentsToProcess.map(agent => ({
            typeName: 'topLevel' as const,
            displayName: agent.displayName ?? agent.login,
            avatarUrl: agent.avatarUrl,
            login: agent.login,
            botGlobalRelayId: agent.id,
          })),
        })
        handleOpenCopilotModal()
      } else {
        if (!assigneesEqual(selectedAssignees, assignees)) {
          updateAssignees(selectedAssignees)
        }
      }
    },
    [assignees, canUseAgentAssignment, updateAssignees, handleOpenCopilotModal],
  )

  // Dispatch to the right handler based on the FF
  const handleAssigneeSelectionChange = useCallback(
    (selectedAssignees: AssigneePickerAssignee[]) => {
      // Intercept unauthorized agent selection: open the auth dialog and abort assignment.
      // Status precedence matches enrichWithAuthState (API > mock).
      const previousAgentLogins = new Set(assignees.filter(a => a.isAgent || a.isCopilot).map(a => a.login))
      const newlyAddedAgent = selectedAssignees.find(
        a => (a.isAgent || a.isCopilot) && !previousAgentLogins.has(a.login),
      )
      const newlyAddedDisplayName =
        newlyAddedAgent?.name ?? newlyAddedAgent?.displayName ?? newlyAddedAgent?.login ?? ''
      const newlyAddedApiStatus = newlyAddedAgent ? agentAuthStatuses.get(newlyAddedAgent.login) : undefined
      const newlyAddedRequiresAuth =
        newlyAddedAgent !== undefined &&
        (newlyAddedApiStatus?.needsAuthorization ??
          authorizationGuard.requiresAuth(newlyAddedAgent.login, newlyAddedDisplayName, newlyAddedAgent.avatarUrl))
      if (newlyAddedAgent && newlyAddedRequiresAuth) {
        authorizationGuard.openDialog({
          slug: newlyAddedAgent.login,
          displayName: newlyAddedDisplayName,
          avatarUrl: newlyAddedAgent.avatarUrl,
          integrationId: newlyAddedApiStatus?.integrationId,
        })
        return
      }

      if (isFeatureEnabled('issue_cca_modal_open')) {
        updateAssigneesWithAgentDetection(selectedAssignees)
      } else {
        updateAssigneesWithCopilotDetection(selectedAssignees)
      }
    },
    [
      assignees,
      authorizationGuard,
      agentAuthStatuses,
      updateAssigneesWithAgentDetection,
      updateAssigneesWithCopilotDetection,
    ],
  )

  const onAssignmentComplete = useAgentAssignmentComplete({
    issueId,
    onIssueUpdate,
  })

  const copilotAssignmentButton = canUseCopilotAssignment ? (
    <CopilotAssignmentButton onClick={handleOpenCopilotModal} />
  ) : undefined

  const isAgentCurrentlyAssigned = assignees.some(assignee => assignee.isAgent || assignee.isCopilot)
  const agentAssignmentButton =
    canUseAgentAssignment && !isAgentCurrentlyAssigned ? (
      <CopilotAssignmentButton onClick={handleOpenCopilotModal} />
    ) : undefined

  // Modal for agent assignment triggered from both button and assignee picker
  // Use canUseAgentAssignment when FF is on (allows modal even when Copilot is already assigned)
  // Fall back to canUseCopilotAssignment for the FF-off path
  const canRenderModal = isFeatureEnabled('issue_cca_modal_open') ? canUseAgentAssignment : canUseCopilotAssignment
  const copilotAssignmentModal =
    canRenderModal && copilotModalOpen ? (
      <CopilotAssignmentModal
        isOpen={copilotModalOpen}
        onClose={handleCloseCopilotModal}
        repository={{
          name: repository.name,
          owner: {
            login: repository.owner.login,
          },
        }}
        issueNumbers={[number]}
        onAssignmentComplete={onAssignmentComplete}
        returnFocusRef={assigneePickerAnchorRef}
        initialAgent={agentQueue.currentAgent}
        agentQueuePosition={agentQueue.total > 1 ? agentQueue.total - agentQueue.remaining.length : undefined}
        agentQueueTotal={agentQueue.total > 1 ? agentQueue.total : undefined}
        initialFormCustomizations={agentQueue.lastFormCustomizations}
      />
    ) : undefined

  const assigneeSectionHeader = useMemo(() => {
    if (readonlyAssignee) {
      return <ReadonlyAssigneesSectionHeader />
    }

    const pickerProps: AssigneeIssuePickerProps = {
      repo,
      owner,
      number,
      anchorElement: (props: React.HTMLAttributes<HTMLElement>, ref?: RefObject<HTMLButtonElement | null>) => (
        <SectionHeader
          title={LABELS.sectionTitles.assignees}
          buttonProps={{
            ...props,
            onClick: e => {
              sendAnalyticsEvent('analytics.click', 'ISSUE_SIDEBAR_ASSIGNEE_SECTION_EDIT_CLICK')
              props.onClick?.(e)
            },
          }}
          ref={(node: HTMLButtonElement | null) => {
            assigneePickerAnchorRef.current = node
            if (typeof ref === 'function') (ref as (node: HTMLButtonElement | null) => void)(node)
            else if (ref) ref.current = node
          }}
        />
      ),
      readonly: readonlyAssignee,
      assignees: enrichedAssignees,
      onSelectionChange: (selectedAssignees: AssigneePickerAssignee[]) =>
        handleAssigneeSelectionChange(selectedAssignees),
      insidePortal: insideSidePanel,
      suggestions: enrichedSuggestions,
    }

    return <AssigneePicker key={`assignee-picker-${pickerInstanceKey}`} {...pickerProps} />
  }, [
    readonlyAssignee,
    repo,
    owner,
    number,
    enrichedAssignees,
    insideSidePanel,
    enrichedSuggestions,
    pickerInstanceKey,
    sendAnalyticsEvent,
    handleAssigneeSelectionChange,
  ])

  return (
    <>
      <AssigneesSection
        sectionHeader={assigneeSectionHeader}
        assignees={assignees}
        agentAssignments={data.agentAssignments}
        issueAgentTasks={issueAgentTasksData?.tasks}
        agents={issueAgentTasksData?.assigned_agents}
        isAgentTasksLoading={isAgentTasksPending}
        onSelfAssignClick={() => (viewerData ? updateAssignees([viewerData]) : noop)}
        readonly={readonlyAssignee}
        copilotAssignmentButton={
          isFeatureEnabled('issue_cca_modal_open') ? agentAssignmentButton : copilotAssignmentButton
        }
      />
      {copilotAssignmentModal}
      {authorizationGuard.dialog}
    </>
  )
}
